import { formatLocale } from "../i18n";
// Single source of truth for currency symbols across the app.
// Settlement currencies the platform supports today; unknown values fall
// back to "$" so the UI never crashes — but the server-side enum is the
// authoritative gate. Keep this list in sync with
// server/lib/payouts/settlementCurrency.js SUPPORTED_SETTLEMENT_CURRENCIES.
const CURRENCY_SYMBOLS: Record<string, string> = {
  usd: "$",
  eur: "€",
  gbp: "£",
  aud: "A$",
  cad: "CA$",
  nzd: "NZ$",
  sgd: "S$",
  hkd: "HK$",
  jpy: "¥",
  mxn: "MX$",
  brl: "R$",
  // Phase B settlement currencies (European). The three Nordic krone share
  // "kr"; multi-currency strings append the ISO code (see KR_AMBIGUOUS).
  sek: "kr",
  dkk: "kr",
  nok: "kr",
  pln: "zł",
  czk: "Kč",
  huf: "Ft",
  ron: "lei",
  chf: "CHF",
};

// Currencies whose symbol conventionally follows the amount ("25 kr", "20 Ft").
const SUFFIX_CURRENCIES = new Set([
  "sek", "dkk", "nok", "pln", "czk", "huf", "ron", "chf",
]);

// The three Nordic krone share the "kr" symbol; multi-currency strings append
// the ISO code so they stay tellable-apart.
const KR_AMBIGUOUS = new Set(["sek", "dkk", "nok"]);

/** True when this currency writes its symbol AFTER the amount ("200 kr"). */
export function isSuffixCurrency(currency?: string | null): boolean {
  return SUFFIX_CURRENCIES.has(String(currency || "usd").toLowerCase());
}

export function currencySymbol(currency?: string | null): string {
  const code = String(currency || "").toLowerCase();
  if (!code) return "$"; // empty/missing → dollar (dashboard default)
  // Unknown currency → uppercased ISO code, never a wrong "$" — matches the
  // server (lib/currency.js) so a non-USD payout is never mislabeled.
  return CURRENCY_SYMBOLS[code] || code.toUpperCase();
}

// Place the symbol on the correct side of the number for this currency.
function placeSymbol(numStr: string, currency?: string | null): string {
  const code = String(currency || "usd").toLowerCase();
  const sym = currencySymbol(code);
  return SUFFIX_CURRENCIES.has(code) ? `${numStr} ${sym}` : `${sym}${numStr}`;
}

/**
 * Format a single amount in one currency with the symbol on the correct side,
 * e.g. `formatSingleAmount(25, "sek")` → `"25.00 kr"`,
 * `formatSingleAmount(190.39, "usd")` → `"$190.39"`.
 */
export function formatSingleAmount(
  amount: number,
  currency?: string | null,
  decimals = 2
): string {
  return placeSymbol((Number(amount) || 0).toFixed(decimals), currency);
}

// Currencies with no minor unit — an amount is always a whole number. The
// app already rounds HUF prices to whole forints (payinConfig roundUpToStep);
// rendering "2000.00 Ft" is as wrong as rendering "$5.0000".
const ZERO_DECIMAL_CURRENCIES = new Set(["huf", "jpy"]);

/**
 * Format one amount for a transaction ROW (history lists, statements): always
 * the currency's natural precision — 2 decimals for USD/EUR/kr, 0 for HUF/JPY
 * — with thousands separators. `$17.50`, `$10.00`, `2,000 Ft`.
 */
export function formatTxnAmount(amount: number, currency?: string | null): string {
  const code = String(currency || "usd").toLowerCase();
  const d = ZERO_DECIMAL_CURRENCIES.has(code) ? 0 : 2;
  return placeSymbol(
    (Number(amount) || 0).toLocaleString(formatLocale(), {
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    }),
    code
  );
}

/**
 * Format an amount for sentence copy (bounds hints, error messages, button
 * labels): thousands separators, decimals only when fractional — e.g.
 * `formatAmountForCopy(200000, "huf")` → `"200,000 Ft"`,
 * `formatAmountForCopy(12.5, "usd")` → `"$12.50"`, `40` → `"$40"`.
 * Display surfaces (pills, stat rows) keep `formatSingleAmount`.
 */
export function formatAmountForCopy(amount: number, currency?: string | null): string {
  const n = Number(amount) || 0;
  const d = n % 1 === 0 ? 0 : 2;
  return placeSymbol(
    n.toLocaleString(formatLocale(), { minimumFractionDigits: d, maximumFractionDigits: d }),
    currency
  );
}

// Priority order for rendering multi-currency money strings. USD and EUR lead
// (today's primary markets); the rest follow the supported settlement-currency
// list. Anything not listed sorts last, alphabetically. Adding a new currency
// to the platform is just one line here (plus its symbol above) — every total
// across the admin dashboard picks it up automatically, so this scales cleanly
// to the 10+ currencies on the roadmap.
const CURRENCY_DISPLAY_ORDER = [
  "usd", "eur", "gbp", "aud", "cad", "nzd", "sgd", "hkd", "jpy", "mxn", "brl",
  "sek", "dkk", "nok", "pln", "czk", "huf", "ron", "chf",
];

function currencyRank(code: string): number {
  const i = CURRENCY_DISPLAY_ORDER.indexOf(String(code || "").toLowerCase());
  return i === -1 ? CURRENCY_DISPLAY_ORDER.length : i;
}

/** Sort comparator for currency codes: display priority, the rest A–Z. */
export function byCurrencyPriority(a: string, b: string): number {
  return currencyRank(a) - currencyRank(b) || a.localeCompare(b);
}

export type MoneyByCurrency = Record<string, number>;

/**
 * Render a per-currency money map as a side-by-side string using the real
 * currency symbols, e.g. `{ usd: 190.39, eur: 0.3 }` → `"$190.39 | €0.30"`.
 *
 * Currencies are NEVER blended or converted — each is shown on its own with its
 * own symbol, in priority order (USD, EUR, …). Entries that round to zero at the
 * chosen precision are omitted, so a USD-only platform still just shows
 * "$190.39". An empty map renders as "$0.00" so a freshly-loaded dashboard never
 * shows a bare symbol.
 */
export function formatMoneyByCurrency(
  byCurrency: MoneyByCurrency,
  opts: { decimals?: number } = {}
): string {
  const decimals = opts.decimals ?? 2;
  const epsilon = 0.5 / Math.pow(10, decimals); // values that round to 0 at this precision
  const parts = Object.entries(byCurrency || {})
    .filter(
      ([, amount]) =>
        typeof amount === "number" && isFinite(amount) && Math.abs(amount) >= epsilon
    )
    .sort((a, b) => currencyRank(a[0]) - currencyRank(b[0]) || a[0].localeCompare(b[0]))
    .map(([code, amount]) => {
      const placed = placeSymbol(amount.toFixed(decimals), code);
      // SEK/DKK/NOK all display "kr" — in a multi-currency string the ISO
      // code is the only thing keeping the three distinct (server
      // lib/currency.js formatWithCode does the same).
      return KR_AMBIGUOUS.has(String(code).toLowerCase())
        ? `${placed} ${String(code).toUpperCase()}`
        : placed;
    });
  return parts.length > 0
    ? parts.join(" | ")
    : placeSymbol((0).toFixed(decimals), "usd");
}

/**
 * The same per-currency map as one string per currency, for a UI that lays
 * the currencies out itself: display priority, each at its natural precision
 * (formatTxnAmount), the three krone with their ISO code, zeros dropped —
 * e.g. `{ eur: 214.5, usd: 40 }` → `["$40.00", "€214.50"]`. Never blended.
 */
export function formatMoneyParts(byCurrency: MoneyByCurrency): string[] {
  return Object.entries(byCurrency || {})
    .filter(([, amount]) => typeof amount === "number" && isFinite(amount) && amount !== 0)
    .sort((a, b) => byCurrencyPriority(a[0], b[0]))
    .map(([code, amount]) => {
      const txt = formatTxnAmount(amount, code);
      return KR_AMBIGUOUS.has(code.toLowerCase()) ? `${txt} ${code.toUpperCase()}` : txt;
    });
}

// Shape of one bucket in a Payout's `totalsByCurrency` map (server: Payout.js).
export interface CurrencyTotals {
  gross: number;
  fee: number;
  net: number;
  ordersCount: number;
  /** Refer & Earn money this payout carried (part of gross, no commission on it). Absent = none. */
  referralBonus?: number;
}

/**
 * Pull one numeric field out of a Payout's `totalsByCurrency` map into a plain
 * `{ currency: amount }` map ready for `formatMoneyByCurrency`. e.g.
 * field="gross" → `{ usd: 12.5, eur: 3 }`.
 */
export function moneyByCurrencyFrom(
  totalsByCurrency: Record<string, CurrencyTotals> | undefined | null,
  field: keyof CurrencyTotals
): MoneyByCurrency {
  const out: MoneyByCurrency = {};
  for (const [cur, t] of Object.entries(totalsByCurrency || {})) {
    const v = t?.[field];
    if (typeof v === "number") out[cur] = v;
  }
  return out;
}

/**
 * Average request value of a payout batch in its SETTLEMENT currency, derived
 * from the batch's own frozen totals (gross / ordersCount of the primary
 * bucket) — the same definition the cashout email uses. The server's
 * `avgRequestValue` is a charge-currency (USD/EUR) number, so labeling IT with
 * the settlement symbol mislabels every pay-in-fix country (GB/CA/GI + the 9
 * Phase B countries); deriving from the totals keeps number and label in the
 * same currency. Falls back to the server avg labeled USD for legacy rows
 * without a usable bucket.
 */
export function formatBatchAvgValue(
  totalsByCurrency: Record<string, CurrencyTotals> | undefined | null,
  fallbackAvg: number
): string {
  const primary = primaryCurrencyOf(totalsByCurrency);
  const bucket = totalsByCurrency?.[primary];
  if (bucket && bucket.ordersCount > 0 && typeof bucket.gross === "number") {
    return formatSingleAmount(bucket.gross / bucket.ordersCount, primary);
  }
  return formatSingleAmount(fallbackAvg || 0, "usd");
}

/**
 * The lead currency of a payout batch (by display priority). Used for figures
 * that the server only sends as a single pre-blended number (e.g. a batch's
 * average request value) so we can at least show the correct symbol. Defaults
 * to "usd" when no currency info is present.
 */
export function primaryCurrencyOf(
  totalsByCurrency: Record<string, unknown> | undefined | null
): string {
  const keys = Object.keys(totalsByCurrency || {});
  if (keys.length === 0) return "usd";
  return keys.sort((a, b) => currencyRank(a) - currencyRank(b))[0];
}
